import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { jsonOrThrow } from '../apiJson';
import { ProductCard } from './ProductCard';
import {
  CategoryListSkeletonSections,
  CategoryProductGridSkeleton,
  landingProductSkeletonCount,
} from './ProductCardSkeleton';
import { subcategorySlug } from '../categorySlug';
import { categoryBrowsePath, subcategoryBrowsePath } from '../categoryPaths';
import { formatCount, formatNameWithCount, formatPriceCollectionRange, formatShortNameWithCount } from '../categoryLabels';
import { PRICE_COLLECTION_TAGLINE } from '../priceCollections';
import { trackSeeAllClick, trackSelectCategory, trackSelectSubcategory } from '../ga';
import {
  getRecentlyViewedProducts,
  subscribeRecentlyViewedProducts,
} from '../recentlyViewedProducts';
import { FacebookFollowLink } from './FacebookFollowLink';
import { CategoryVoteModal } from './CategoryVoteModal';
import { formatDealsEditionDateShort } from '../dealsEditionDate';
import {
  listPremiumShops,
  premiumShopWebsiteHost,
  resolveFeaturedPremiumShopForHomepage,
} from '../premiumShops';
import { FeaturedPremiumShopSection } from './FeaturedPremiumShopSection';

const LAZY_LOAD_ROOT_MARGIN = '400px 0px';
const DEALS_HOME_MOBILE_PREVIEW = 4;
const DEALS_HOME_DESKTOP_PREVIEW = 9;
const DEALS_HOME_MOBILE_INSERT_AFTER = 3;
const DEALS_HOME_DESKTOP_INSERT_AFTER = 2;
const DEALS_OF_THE_DAY_PATH = '/deal-of-the-day';
/** Matches `.landing-categories` 2-column grid breakpoint. */
const LANDING_WIDE_MQ = '(min-width: 50rem)';

/**
 * Build a homepage deals preview by round-robin across categories
 * (1st of each, then 2nd of each, …) up to maxProducts.
 * @param {Array<{ products?: unknown[] }>} groups
 * @param {number} maxProducts
 * @param {string} [validFor]
 * @returns {{ products: object[], total: number, validFor: string }}
 */
function pickDealsHomePreview(groups, maxProducts = DEALS_HOME_MOBILE_PREVIEW, validFor = '') {
  const lists = [];
  let total = 0;

  for (const group of groups) {
    const list = Array.isArray(group?.products) ? group.products : [];
    total += list.length;
    if (list.length > 0) {
      lists.push(list);
    }
  }

  const products = [];
  let depth = 0;
  while (products.length < maxProducts) {
    let added = false;
    for (const list of lists) {
      if (depth < list.length) {
        products.push(list[depth]);
        added = true;
        if (products.length >= maxProducts) {
          break;
        }
      }
    }
    if (!added) {
      break;
    }
    depth += 1;
  }

  return {
    products,
    total,
    validFor: typeof validFor === 'string' ? validFor : '',
  };
}

function dealsHomeProductKey(product) {
  const id = product?.id;
  if (typeof id === 'string' && id.trim() !== '') {
    return id.trim();
  }
  if (typeof id === 'number' && Number.isFinite(id)) {
    return String(id);
  }
  const url = typeof product?.url === 'string' ? product.url.trim() : '';
  return url !== '' ? url : 'product';
}

function useLandingWideLayout() {
  const [wide, setWide] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(LANDING_WIDE_MQ).matches : false,
  );

  useEffect(() => {
    const mq = window.matchMedia(LANDING_WIDE_MQ);
    function sync() {
      setWide(mq.matches);
    }
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  return wide;
}

function splitSubcategoryRows(subcategories) {
  if (subcategories.length === 0) {
    return [];
  }
  const midpoint = Math.ceil(subcategories.length / 2);
  return [
    subcategories.slice(0, midpoint),
    subcategories.slice(midpoint),
  ].filter((row) => row.length > 0);
}

function cx(...parts) {
  return parts.filter(Boolean).join(' ');
}

function recentlyViewedProductKey(product) {
  const url = typeof product?.url === 'string' ? product.url.trim() : '';
  if (url !== '') {
    return url;
  }

  const id = product?.id;
  if (typeof id === 'string' && id.trim() !== '') {
    return id.trim();
  }
  if (typeof id === 'number' && Number.isFinite(id)) {
    return String(id);
  }

  return product?.title ?? 'product';
}

export function RecentlyViewedCategorySection({ products }) {
  if (products.length === 0) {
    return null;
  }

  return (
    <section
      className="landing-categories__section landing-categories__section--recent"
      aria-labelledby="landing-recent-viewed-heading"
    >
      <header className="landing-categories__section-header">
        <h2
          id="landing-recent-viewed-heading"
          className="landing-categories__heading"
        >
          Your Recently Viewed Products
        </h2>
      </header>
      <div
        className="landing-categories__product-scroll"
        aria-label="Recently viewed products"
      >
        <ul className="landing-categories__product-scroll-track landing-categories__mini-cards">
          {products.map((product) => (
            <li key={recentlyViewedProductKey(product)} className="landing-categories__cell">
              <ProductCard
                product={product}
                layout="grid"
                outboundCampaign="homepage_recent"
                analyticsContext={{ source: 'homepage' }}
              />
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/**
 * Homepage featured premium shop section.
 */
function FeaturedPremiumShopHomeSection({
  shop,
  layoutVariant = 'classic',
  wideLayout = false,
}) {
  return (
    <FeaturedPremiumShopSection
      shop={shop}
      layoutVariant={layoutVariant}
      wideLayout={wideLayout}
      outboundCampaign="homepage_featured_premium"
      analyticsSource="home"
    />
  );
}

function useFeaturedPremiumShop() {
  const [shop, setShop] = useState(null);

  useEffect(() => {
    if (listPremiumShops().length === 0) {
      return undefined;
    }

    let cancelled = false;
    resolveFeaturedPremiumShopForHomepage().then((resolved) => {
      if (!cancelled) {
        setShop(resolved);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return shop;
}

/**
 * Homepage Deal of the Day: 4+View all (1-col) / 9+View all full-width (2-col).
 */
function DealsOfTheDayHomeSection({ layoutVariant = 'classic', wideLayout = false }) {
  const sectionRef = useRef(null);
  const fetchStartedRef = useRef(false);
  const [preview, setPreview] = useState(null);
  const engaging = layoutVariant === 'engaging';
  const previewLimit = wideLayout
    ? DEALS_HOME_DESKTOP_PREVIEW
    : DEALS_HOME_MOBILE_PREVIEW;

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting || fetchStartedRef.current) {
          return;
        }
        fetchStartedRef.current = true;

        fetch('/api/deals-of-the-day.php', { cache: 'no-store' })
          .then((res) => jsonOrThrow(res))
          .then((data) => {
            const groups = Array.isArray(data?.dealsByCategory) ? data.dealsByCategory : [];
            const validFor = typeof data?.validFor === 'string' ? data.validFor : '';
            setPreview(pickDealsHomePreview(groups, DEALS_HOME_DESKTOP_PREVIEW, validFor));
          })
          .catch(() => {
            setPreview({ products: [], total: 0, validFor: '' });
          });
      },
      { rootMargin: LAZY_LOAD_ROOT_MARGIN, threshold: 0 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const loading = preview === null;
  const allProducts = preview?.products ?? [];
  const products = allProducts.slice(0, previewLimit);
  const total = preview?.total ?? 0;
  const moreCount = Math.max(1, total - products.length);
  const editionDate = formatDealsEditionDateShort(preview?.validFor ?? '');
  const titleText = editionDate
    ? `Deal of the Day · ${editionDate}`
    : 'Deal of the Day';

  if (!loading && allProducts.length === 0) {
    return null;
  }

  const productCells = products.map((product, index) => (
    <li
      key={dealsHomeProductKey(product) || `deal-home-${index}`}
      className="landing-categories__cell"
    >
      <ProductCard
        product={product}
        layout="grid"
        outboundCampaign="homepage_deal_of_the_day"
        analyticsContext={{ source: 'sale' }}
      />
    </li>
  ));

  const viewAllCell = (
    <li className="landing-categories__cell landing-categories__cell--cta">
      <Link
        to={DEALS_OF_THE_DAY_PATH}
        className="landing-categories__more-tile"
        aria-label={`View all ${formatCount(total)} Deal of the Day products`}
        onClick={() => trackSeeAllClick('deal-of-the-day', 'home')}
      >
        <span className="landing-categories__more-label">View all</span>
        <span className="landing-categories__more-plus" aria-hidden="true">
          +{formatCount(moreCount)}
        </span>
      </Link>
    </li>
  );

  return (
    <section
      ref={sectionRef}
      className={cx(
        'landing-categories__section',
        'landing-categories__section--deals-home',
        wideLayout && 'landing-categories__section--deals-home-wide',
      )}
      aria-labelledby="landing-deals-of-the-day-heading"
    >
      <header className="landing-categories__section-header">
        <div className="landing-categories__heading-block">
          <h2
            id="landing-deals-of-the-day-heading"
            className="landing-categories__heading"
          >
            <Link
              className="landing-categories__heading-link"
              to={DEALS_OF_THE_DAY_PATH}
              aria-label={
                total > 0
                  ? `${titleText}, ${formatCount(total)} featured deals`
                  : titleText
              }
              onClick={() => trackSelectCategory('deal-of-the-day', 'home_grid')}
            >
              {titleText}
            </Link>
          </h2>
        </div>
        {engaging && formatCount(total) && (
          <span className="landing-categories__heading-count">
            {formatCount(total)}
          </span>
        )}
      </header>
      {loading ? (
        <CategoryProductGridSkeleton count={previewLimit} />
      ) : wideLayout ? (
        <div
          className="landing-categories__product-scroll"
          aria-label="Deal of the Day products"
        >
          <ul className="landing-categories__product-scroll-track landing-categories__mini-cards">
            {productCells}
            {viewAllCell}
          </ul>
        </div>
      ) : (
        <ul className="landing-categories__grid landing-categories__mini-cards">
          {productCells}
          {viewAllCell}
        </ul>
      )}
    </section>
  );
}

/**
 * Split category list into regular sections and consecutive price-collection bands.
 * @returns {Array<{ type: 'regular', category: object } | { type: 'price', categories: object[] }>}
 */
function landingCategorySegments(categories) {
  /** @type {Array<{ type: 'regular', category: object } | { type: 'price', categories: object[] }>} */
  const segments = [];
  /** @type {object[] | null} */
  let priceBatch = null;

  for (const category of categories) {
    if (category.priceCollection === true) {
      if (!priceBatch) {
        priceBatch = [];
      }
      priceBatch.push(category);
      continue;
    }

    if (priceBatch) {
      segments.push({ type: 'price', categories: priceBatch });
      priceBatch = null;
    }
    segments.push({ type: 'regular', category });
  }

  if (priceBatch) {
    segments.push({ type: 'price', categories: priceBatch });
  }

  return segments;
}

function subcategoryLinkAriaLabel(name, count) {
  const formatted = formatCount(count);
  if (!formatted) {
    return name;
  }
  return `${name}, ${formatted} products`;
}

const ENGAGING_SUBCATEGORY_MAX_ROWS = 2;

function engagingSubcategoryListExceedsMaxRows(container, maxRows) {
  const lineHeight = parseFloat(getComputedStyle(container).lineHeight);
  if (!Number.isFinite(lineHeight) || lineHeight <= 0) {
    return container.scrollHeight > container.clientHeight + 1;
  }
  return container.scrollHeight > lineHeight * maxRows + 1;
}

function fillEngagingSubcategoryMeasureList(container, items, count, includeExpandButton) {
  container.replaceChildren();

  for (let index = 0; index < count; index += 1) {
    const sub = items[index];
    const item = document.createElement('li');
    item.className = 'landing-categories__sub-item';

    const link = document.createElement('span');
    link.className = 'landing-categories__sub-link';

    const label = document.createElement('span');
    label.className = 'landing-categories__sub-label';
    label.textContent = sub.name;

    link.appendChild(label);
    item.appendChild(link);
    container.appendChild(item);
  }

  if (includeExpandButton) {
    const expandItem = document.createElement('li');
    expandItem.className =
      'landing-categories__sub-item landing-categories__sub-item--expand';

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'landing-categories__sub-expand';
    button.textContent = 'View more';

    expandItem.appendChild(button);
    container.appendChild(expandItem);
  }
}

function measureEngagingSubcategoryVisibleCount(navEl, measureEl, items) {
  measureEl.style.width = `${navEl.clientWidth}px`;

  fillEngagingSubcategoryMeasureList(measureEl, items, items.length, false);
  if (!engagingSubcategoryListExceedsMaxRows(measureEl, ENGAGING_SUBCATEGORY_MAX_ROWS)) {
    return { visibleCount: items.length, hasOverflow: false };
  }

  let low = 0;
  let high = items.length;
  while (low < high) {
    const mid = Math.ceil((low + high) / 2);
    fillEngagingSubcategoryMeasureList(measureEl, items, mid, true);
    if (engagingSubcategoryListExceedsMaxRows(measureEl, ENGAGING_SUBCATEGORY_MAX_ROWS)) {
      high = mid - 1;
    } else {
      low = mid;
    }
  }

  return { visibleCount: low, hasOverflow: true };
}

function EngagingInlineSubcategoryList({ categorySlug, categoryName, subcategories }) {
  const navRef = useRef(null);
  const measureRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [visibleCount, setVisibleCount] = useState(subcategories.length);
  const [hasOverflow, setHasOverflow] = useState(false);
  const items = useMemo(
    () =>
      [...subcategories].sort(
        (a, b) => (Number(b.count) || 0) - (Number(a.count) || 0),
      ),
    [subcategories],
  );
  const itemsKey = useMemo(
    () => items.map((sub) => sub.name).join('\0'),
    [items],
  );

  useEffect(() => {
    setExpanded(false);
  }, [categorySlug, itemsKey]);

  useLayoutEffect(() => {
    const navEl = navRef.current;
    const measureEl = measureRef.current;
    if (!navEl || !measureEl || items.length === 0) {
      setVisibleCount(items.length);
      setHasOverflow(false);
      return undefined;
    }

    if (expanded) {
      setVisibleCount(items.length);
      return undefined;
    }

    const runMeasure = () => {
      const result = measureEngagingSubcategoryVisibleCount(navEl, measureEl, items);
      setVisibleCount(result.visibleCount);
      setHasOverflow(result.hasOverflow);
    };

    runMeasure();
    const observer = new ResizeObserver(runMeasure);
    observer.observe(navEl);
    return () => observer.disconnect();
  }, [items, itemsKey, expanded]);

  const shownItems = expanded ? items : items.slice(0, visibleCount);
  const showExpandControl = hasOverflow;

  return (
    <nav
      ref={navRef}
      className="landing-categories__sub-nav landing-categories__sub-nav--inline"
      aria-label={`${categoryName} subcategories`}
    >
      <ul
        className={cx(
          'landing-categories__sub-list',
          'landing-categories__sub-list--inline',
        )}
      >
        {shownItems.map((sub) => (
          <li key={sub.name} className="landing-categories__sub-item">
            <Link
              className="landing-categories__sub-link"
              to={subcategoryBrowsePath(categorySlug, subcategorySlug(sub))}
              aria-label={subcategoryLinkAriaLabel(sub.name, sub.count)}
              onClick={() =>
                trackSelectSubcategory(categorySlug, sub.name, 'home_subcategory_link')
              }
            >
              <span className="landing-categories__sub-label">{sub.name}</span>
            </Link>
          </li>
        ))}
        {showExpandControl && (
          <li className="landing-categories__sub-item landing-categories__sub-item--expand">
            <button
              type="button"
              className="landing-categories__sub-expand"
              aria-expanded={expanded}
              onClick={() => setExpanded((open) => !open)}
            >
              {expanded ? 'View less' : 'View more'}
            </button>
          </li>
        )}
      </ul>
      <ul
        ref={measureRef}
        className={cx(
          'landing-categories__sub-list',
          'landing-categories__sub-list--inline',
          'landing-categories__sub-list--measure',
        )}
        aria-hidden="true"
      />
    </nav>
  );
}

function CategorySubcategoryList({
  categorySlug,
  categoryName,
  subcategories,
  layoutVariant = 'classic',
}) {
  const engaging = layoutVariant === 'engaging';

  if (subcategories.length === 0) {
    return null;
  }

  if (engaging) {
    return (
      <EngagingInlineSubcategoryList
        categorySlug={categorySlug}
        categoryName={categoryName}
        subcategories={subcategories}
      />
    );
  }

  const rows = splitSubcategoryRows(subcategories);

  return (
    <div
      className="landing-categories__pills-scroll-wrap"
      aria-label={`${categoryName} subcategories`}
    >
      <div className="landing-categories__pills-scroll">
        <div className="landing-categories__pills-track">
        {rows.map((row, rowIndex) => (
          <div key={rowIndex} className="landing-categories__pills-row">
            {row.map((sub) => (
              <Link
                key={sub.name}
                className="landing-categories__pill"
                to={subcategoryBrowsePath(categorySlug, subcategorySlug(sub))}
                aria-label={subcategoryLinkAriaLabel(sub.name, sub.count)}
                onClick={() =>
                  trackSelectSubcategory(categorySlug, sub.name, 'home_subcategory_pill')
                }
              >
                <span className="landing-categories__pill-label">{sub.name}</span>
                {formatCount(sub.count) && (
                  <span className="landing-categories__pill-count" aria-hidden="true">
                    {formatCount(sub.count)}
                  </span>
                )}
              </Link>
            ))}
          </div>
        ))}
        </div>
      </div>
    </div>
  );
}

function CategorySectionHeader({
  category,
  seeAllHref,
  layoutVariant = 'classic',
}) {
  const isPriceCollection = category.priceCollection === true;
  const count = category.count ?? category.productCount;
  const priceRangeLabel = isPriceCollection
    ? formatPriceCollectionRange(category.priceMin, category.priceMax)
    : '';
  const engaging = layoutVariant === 'engaging';

  return (
    <header className="landing-categories__section-header">
      {isPriceCollection && (
        <span className="landing-categories__price-badge">By price</span>
      )}
      <div className="landing-categories__heading-block">
        <h2 className="landing-categories__heading">
          <Link
            className="landing-categories__heading-link"
            to={seeAllHref}
            aria-label={formatNameWithCount(category.name, count)}
            onClick={() => trackSelectCategory(category.slug, 'home_grid')}
          >
            {engaging
              ? category.name
              : formatShortNameWithCount(category.name, count)}
          </Link>
        </h2>
        {priceRangeLabel && (
          <p className="landing-categories__price-range">{priceRangeLabel}</p>
        )}
      </div>
      {engaging && formatCount(count) && (
        <span className="landing-categories__heading-count">
          {formatCount(count)}
        </span>
      )}
    </header>
  );
}

export function CategoryProductGrid({
  name,
  count,
  seeAllHref,
  products,
  outboundCampaign = 'homepage',
  categorySlug = '',
  seeAllSourcePage = 'home',
  analyticsSource = null,
}) {
  const countNum = Number(count);
  const hasCount = Number.isFinite(countNum) && countNum > 0;
  const hasMore = hasCount && countNum > products.length;
  const displayProducts =
    hasMore && products.length >= 3 ? products.slice(0, 3) : products;
  const moreCount = hasMore
    ? Math.max(1, countNum - displayProducts.length)
    : 0;
  const impressionSource =
    typeof analyticsSource === 'string' && analyticsSource.trim() !== ''
      ? analyticsSource.trim()
      : outboundCampaign;

  if (products.length === 0) {
    return <p className="landing-categories__empty">Coming soon.</p>;
  }

  return (
    <ul className="landing-categories__grid landing-categories__mini-cards">
      {displayProducts.map((product) => (
        <li key={product.id} className="landing-categories__cell">
          <ProductCard
            product={product}
            layout="grid"
            outboundCampaign={outboundCampaign}
            analyticsContext={{
              source: impressionSource,
              shopCategorySlug: categorySlug || null,
            }}
          />
        </li>
      ))}
      {hasMore && (
        <li className="landing-categories__cell landing-categories__cell--cta">
          <Link
            to={seeAllHref}
            className="landing-categories__more-tile"
            aria-label={`View all ${formatCount(countNum)} ${name} products`}
            onClick={() => {
              if (categorySlug) {
                trackSeeAllClick(categorySlug, seeAllSourcePage);
              }
            }}
          >
            <span className="landing-categories__more-label">View all</span>
            <span
              className="landing-categories__more-plus"
              aria-hidden="true"
            >
              +{formatCount(moreCount)}
            </span>
          </Link>
        </li>
      )}
    </ul>
  );
}

function LazyCategorySection({ category, layoutVariant = 'classic' }) {
  const sectionRef = useRef(null);
  const fetchStartedRef = useRef(false);
  const [products, setProducts] = useState(null);
  const countNum = Number(category.productCount ?? category.count);
  const needsProducts = Number.isFinite(countNum) && countNum > 0;
  const skeletonCount = landingProductSkeletonCount(category.productCount ?? category.count);
  const seeAllHref = categoryBrowsePath(category.slug);
  const subcategories = Array.isArray(category.subcategories) ? category.subcategories : [];

  useEffect(() => {
    if (!needsProducts) {
      return undefined;
    }

    const el = sectionRef.current;
    if (!el) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting || fetchStartedRef.current) {
          return;
        }
        fetchStartedRef.current = true;

        fetch(
          `/api/landing-products.php?shop=${encodeURIComponent(category.slug)}`,
          { cache: 'no-store' },
        )
          .then((res) => jsonOrThrow(res))
          .then((data) => {
            const byCategory = data?.productsByCategory;
            const list =
              byCategory && typeof byCategory === 'object'
                ? byCategory[category.slug]
                : [];
            const products = Array.isArray(list) ? list : [];
            setProducts(products);
          })
          .catch(() => {
            setProducts([]);
          });
      },
      { rootMargin: LAZY_LOAD_ROOT_MARGIN, threshold: 0 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [category.slug, needsProducts]);

  const showSkeleton = needsProducts && products === null;
  const isPriceCollection = category.priceCollection === true;

  const engaging = layoutVariant === 'engaging';

  return (
    <section
      ref={sectionRef}
      className={cx(
        'landing-categories__section',
        isPriceCollection && 'landing-categories__section--price-collection',
      )}
    >
      <CategorySectionHeader
        category={category}
        seeAllHref={seeAllHref}
        layoutVariant={layoutVariant}
      />
      {subcategories.length > 0 && engaging && (
        <CategorySubcategoryList
          categorySlug={category.slug}
          categoryName={category.name}
          subcategories={subcategories}
          layoutVariant={layoutVariant}
        />
      )}
      {showSkeleton ? (
        <CategoryProductGridSkeleton count={skeletonCount} />
      ) : needsProducts ? (
        <CategoryProductGrid
          name={category.name}
          count={category.count ?? category.productCount}
          seeAllHref={seeAllHref}
          products={products}
          categorySlug={category.slug}
          seeAllSourcePage="home"
        />
      ) : (
        <p className="landing-categories__empty">Coming soon.</p>
      )}
      {subcategories.length > 0 && !engaging && (
        <CategorySubcategoryList
          categorySlug={category.slug}
          categoryName={category.name}
          subcategories={subcategories}
          layoutVariant={layoutVariant}
        />
      )}
    </section>
  );
}

function PriceCollectionBand({ categories, layoutVariant = 'classic' }) {
  return (
    <div className="landing-categories__price-band">
      <div className="landing-categories__price-band-header">
        <p className="landing-categories__price-band-eyebrow">
          Shop by price
          <span className="landing-categories__price-band-subtitle">
            {' '}
            - {PRICE_COLLECTION_TAGLINE}
          </span>
        </p>
      </div>
      <div className="landing-categories__price-band-sections">
        {categories.map((category) => (
          <LazyCategorySection
            key={category.slug}
            category={category}
            layoutVariant={layoutVariant}
          />
        ))}
      </div>
    </div>
  );
}

function LandingFacebookSection() {
  return (
    <section
      className="landing-categories__facebook"
      aria-labelledby="landing-facebook-heading"
    >
      <h2 id="landing-facebook-heading" className="landing-categories__facebook-heading">
        Follow on Facebook
      </h2>
      <p className="landing-categories__facebook-lede">
        See Malta deals first — price drops, new local shops, and bargains
        we spot before they&apos;re gone.
      </p>
      <FacebookFollowLink
        location="home"
        className="landing-categories__facebook-btn"
      />
    </section>
  );
}

function ComingSoonSection({ categories }) {
  const [voteCategory, setVoteCategory] = useState(null);

  if (categories.length === 0) {
    return null;
  }

  return (
    <>
      <section
        className="landing-categories__coming-soon"
        aria-labelledby="landing-coming-soon-heading"
      >
        <h2
          id="landing-coming-soon-heading"
          className="landing-categories__coming-soon-heading"
        >
          Which category should we add next?
        </h2>
        <p className="landing-categories__coming-soon-lede">
          These categories aren&apos;t live yet — vote for your pick.
        </p>
        <ul className="landing-categories__coming-soon-list">
          {categories.map((c) => (
            <li key={c.slug} className="landing-categories__coming-soon-item">
              <span className="landing-categories__coming-soon-name">{c.name}</span>
              <button
                type="button"
                className="landing-categories__coming-soon-vote"
                onClick={() => setVoteCategory(c)}
              >
                Vote
              </button>
            </li>
          ))}
        </ul>
      </section>
      {voteCategory && (
        <CategoryVoteModal
          categoryName={voteCategory.name}
          categorySlug={voteCategory.slug}
          onClose={() => setVoteCategory(null)}
        />
      )}
    </>
  );
}

export function CategoryList({
  categories,
  comingSoonCategories = [],
  categoriesLoading,
  errorMessage,
  hideRecentlyViewed = false,
  layoutVariant = 'classic',
  showDealsOfTheDayHome = false,
}) {
  const [recentlyViewed, setRecentlyViewed] = useState(() => getRecentlyViewedProducts());
  const wideLayout = useLandingWideLayout();
  const featuredPremiumShop = useFeaturedPremiumShop();
  const dealsInsertAfter = wideLayout
    ? DEALS_HOME_DESKTOP_INSERT_AFTER
    : DEALS_HOME_MOBILE_INSERT_AFTER;

  useEffect(() => subscribeRecentlyViewedProducts(setRecentlyViewed), []);

  if (categoriesLoading) {
    return (
      <>
        <span className="sr-only" role="status">
          Loading categories
        </span>
        <div
          className={cx(
            'landing-categories',
            'landing-recent',
            layoutVariant === 'engaging' && 'landing-categories--engaging',
          )}
          aria-busy="true"
        >
          {!hideRecentlyViewed && (
            <RecentlyViewedCategorySection products={recentlyViewed} />
          )}
          <CategoryListSkeletonSections />
        </div>
      </>
    );
  }

  if (
    errorMessage
    && categories.length === 0
    && comingSoonCategories.length === 0
    && recentlyViewed.length === 0
    && !showDealsOfTheDayHome
    && !featuredPremiumShop
  ) {
    return (
      <p
        className="landing-categories__status landing-categories__status--error"
        role="alert"
      >
        {errorMessage}
      </p>
    );
  }

  if (
    categories.length === 0
    && comingSoonCategories.length === 0
    && recentlyViewed.length === 0
    && !showDealsOfTheDayHome
    && !featuredPremiumShop
  ) {
    return (
      <p className="landing-categories__status">No categories to show.</p>
    );
  }

  const showCategorySections =
    categories.length > 0
    || (!hideRecentlyViewed && recentlyViewed.length > 0)
    || showDealsOfTheDayHome
    || Boolean(featuredPremiumShop);

  const segments = landingCategorySegments(categories);
  const categoryNodes = [];
  let regularCount = 0;
  let dealsInserted = false;
  let featuredPremiumInserted = false;

  const dealsNode = showDealsOfTheDayHome ? (
    <DealsOfTheDayHomeSection
      key="deal-of-the-day-home"
      layoutVariant={layoutVariant}
      wideLayout={wideLayout}
    />
  ) : null;

  const featuredPremiumNode = featuredPremiumShop ? (
    <FeaturedPremiumShopHomeSection
      key={`featured-premium-${premiumShopWebsiteHost(featuredPremiumShop)}`}
      shop={featuredPremiumShop}
      layoutVariant={layoutVariant}
      wideLayout={wideLayout}
    />
  ) : null;

  for (const segment of segments) {
    if (segment.type === 'price') {
      if (dealsNode && !dealsInserted && regularCount >= dealsInsertAfter) {
        categoryNodes.push(dealsNode);
        dealsInserted = true;
      }
      categoryNodes.push(
        <PriceCollectionBand
          key={`price-band-${segment.categories.map((c) => c.slug).join('-')}`}
          categories={segment.categories}
          layoutVariant={layoutVariant}
        />,
      );
      if (featuredPremiumNode && !featuredPremiumInserted) {
        categoryNodes.push(featuredPremiumNode);
        featuredPremiumInserted = true;
      }
      continue;
    }

    regularCount += 1;
    categoryNodes.push(
      <LazyCategorySection
        key={segment.category.slug}
        category={segment.category}
        layoutVariant={layoutVariant}
      />,
    );
    if (dealsNode && !dealsInserted && regularCount === dealsInsertAfter) {
      categoryNodes.push(dealsNode);
      dealsInserted = true;
    }
  }

  if (dealsNode && !dealsInserted) {
    categoryNodes.push(dealsNode);
  }

  if (featuredPremiumNode && !featuredPremiumInserted) {
    categoryNodes.push(featuredPremiumNode);
  }

  return (
    <div
      className={cx(
        'landing-categories',
        'landing-recent',
        layoutVariant === 'engaging' && 'landing-categories--engaging',
      )}
      aria-label={showCategorySections ? 'Browse by category' : undefined}
    >
      {showCategorySections && (
        <>
          {!hideRecentlyViewed && (
            <RecentlyViewedCategorySection products={recentlyViewed} />
          )}
          {categoryNodes}
        </>
      )}
      <ComingSoonSection categories={comingSoonCategories} />
      <LandingFacebookSection />
    </div>
  );
}
